// Win Tool (launcher) HTTP fetchers used by Playbooks — the seam the web tests
// mock (`vi.mock('…/mini-apps/playbooks/launcher-api-client')`). Kept separate
// from the hooks in `launcher-api.ts` so a test can stub the network without
// re-implementing react-query wiring. mini-apps/CLAUDE.md lane 2: never import
// `../launcher/` for this.
import type { Campaign, CampaignMember } from '../_shared/launcher-campaign.types';
import {
  fetchLauncherCampaignMembers,
  fetchLauncherSyncStatus,
  syncLauncherCampaignMembers,
  type LauncherMemberSyncResult,
  type LauncherSyncStatus,
} from '../_shared/launcher-campaign-member-schema';
import { fetchLauncherCampaigns, fetchLauncherCampaign } from '../_shared/launcher-campaign-schema';

type ApiFetch = (input: RequestInfo, init?: RequestInit) => Promise<Response>;

export type { LauncherSyncStatus };

/**
 * Campaign reads delegate to `_shared/launcher-campaign-schema.ts` — the one
 * PARSED fetcher launcher, playbooks and playhouse all share, so the cache
 * under `['launcher','campaigns']` can only ever hold one shape. Before QA
 * round 8 these returned the raw JSON body unparsed, which is why a Lead-Scanner
 * row cached under that key reached `PlaybooksHome` and crashed it at
 * `member_count.toLocaleString()`. These wrappers stay because they are the
 * seam the web tests mock.
 */
export async function fetchCampaigns(apiFetch: ApiFetch): Promise<Campaign[]> {
  return fetchLauncherCampaigns(apiFetch);
}

export async function fetchCampaign(apiFetch: ApiFetch, id: string): Promise<Campaign> {
  return fetchLauncherCampaign(apiFetch, id);
}

export async function fetchCampaignMembers(apiFetch: ApiFetch, id: string): Promise<CampaignMember[]> {
  return fetchLauncherCampaignMembers(apiFetch, id);
}

export async function fetchSyncStatus(apiFetch: ApiFetch): Promise<LauncherSyncStatus> {
  return fetchLauncherSyncStatus(apiFetch);
}

export async function triggerCampaignSync(apiFetch: ApiFetch): Promise<void> {
  await apiFetch('/api/tools/launcher/campaigns/sync', { method: 'POST', timeoutMs: 60_000 } as RequestInit);
}

export type { LauncherMemberSyncResult };

/**
 * How long Playbooks waits for the live member sync before falling back to
 * the mirror. The workflow's own `workflowTimeoutMS` (30 min,
 * `LAUNCHER_MEMBER_SYNC_TIMEOUT_MS`) is a cancel backstop for a wedged run,
 * not an expected duration: `queue-definitions.ts` sizes a run at seconds to
 * a couple of minutes, and launcher's own Refresh button waits 60 s
 * (`mini-apps/launcher/api-client.ts#syncCampaignMembers`). Twice that covers
 * a slow Middleman read plus a short wait behind another campaign on the
 * concurrency-1 `launcher-member-sync` queue. The server keeps syncing after
 * a client abort, so the mirror still ends up fresh for the next read.
 */
export const CAMPAIGN_MEMBER_SYNC_TIMEOUT_MS = 120_000;

/**
 * Live, awaited resync of ONE campaign's members from Salesforce into the
 * `launcher_sfdc_campaign_members` mirror — launcher's own "Refresh"
 * mechanism (`triggerMemberSyncAndWait(id, 'member_manual')`), deliberately
 * NOT deduplicated per call. `apiFetch` throws a `MiniAppFetchError` on a
 * non-2xx (its `userMessage` is the server's `problem()` detail) and rejects
 * with an `AbortError` on timeout; both propagate to the caller unchanged.
 */
export async function triggerCampaignMemberSync(
  apiFetch: ApiFetch,
  id: string,
): Promise<LauncherMemberSyncResult> {
  return syncLauncherCampaignMembers(apiFetch, id, CAMPAIGN_MEMBER_SYNC_TIMEOUT_MS);
}
